export function normalizeWebsite(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {
    return "";
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function isRepeatedCharLabel(label: string): boolean {
  if (label.length < 3) {
    return false;
  }
  const first = label[0].toLowerCase();
  return label.toLowerCase().split("").every((char) => char === first);
}

function hasValidWebsiteHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === "localhost") {
    return true;
  }

  if (!host.includes(".")) {
    return false;
  }

  const labels = host.split(".").filter(Boolean);
  if (labels.length < 2) {
    return false;
  }

  const tld = labels.at(-1) ?? "";
  if (!/^[a-z]{2,24}$/i.test(tld) || isRepeatedCharLabel(tld)) {
    return false;
  }

  if (
    !labels.every((label) =>
      /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label),
    )
  ) {
    return false;
  }

  const domainLabels = labels.slice(0, -1);
  const meaningfulLabels = domainLabels.filter((label) => label !== "www");
  const labelsToCheck =
    meaningfulLabels.length > 0 ? meaningfulLabels : domainLabels;

  return labelsToCheck.some((label) => !isRepeatedCharLabel(label));
}

/**
 * Accepts `http://`, `https://`, or `www.` prefixed URLs (e.g. www.google.com,
 * https://google.com). A bare domain with no prefix at all (e.g. "google.com") is
 * rejected — it's too easy to mistype a random word/phrase as a domain otherwise.
 */
export function isValidWebsiteUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) {
    return false;
  }

  if (!/^(https?:\/\/|www\.)/i.test(trimmed)) {
    return false;
  }

  if (
    trimmed.startsWith(".") ||
    trimmed.endsWith(".") ||
    trimmed.includes("..")
  ) {
    return false;
  }

  try {
    const parsed = new URL(normalizeWebsite(trimmed));
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return false;
    }

    if (parsed.username || parsed.password) {
      return false;
    }

    return hasValidWebsiteHost(parsed.hostname);
  } catch {
    return false;
  }
}
